import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import Select, { createFilter, SingleValue } from 'react-select';
import { TypeaheadOption } from 'src/ui/shared/components/forms/controls/react-selects/TypeaheadControl';
import { locationConstants } from './location.constants';
import { LocationInnerFormData } from '../location-form.models';

const { getSelectStyles } = locationConstants;

interface Props {
    id: string;
    options: TypeaheadOption[];
    filter?: ReturnType<typeof createFilter>;
    placeholder?: string;
    required?: boolean;
}

export const CountryLocationControl = ({
    id,
    options,
    placeholder,
    filter,
    required,
}: Props): React.ReactElement => {
    const { setValue, watch, register } = useFormContext<LocationInnerFormData>();

    useEffect(() => {
        register(`${id}.countryCode`, required ? { required: true } : undefined);
    }, [id, register, required]);

    const formValue = watch(`${id}.countryCode`);

    const getValueOption = useCallback(
        (list: TypeaheadOption[]) => {
            return list.find(c => c.value === formValue) || null;
        },
        [formValue]
    );

    const onChangeInner = useCallback(
        (option: SingleValue<TypeaheadOption>) => {
            if (!option) {
                return;
            }

            setValue(
                id,
                {
                    id,
                    countryCode: option.value,
                    city: null,
                    postcode: null,
                    region: null,
                    street: null,
                },
                {
                    shouldDirty: true,
                    shouldValidate: true,
                }
            );
        },
        [id, setValue]
    );

    const innerPlaceHolder = useMemo(() => {
        return required ? `${placeholder} *` : placeholder;
    }, [placeholder, required]);

    const selectStyles = getSelectStyles(false);

    return (
        <Select
            value={getValueOption(options)}
            placeholder={innerPlaceHolder}
            options={options}
            filterOption={filter}
            onChange={onChangeInner}
            {...selectStyles}
        />
    );
};
